/**
 * EncounterAuditsTableHeader Component
 *
 * Responsive header row for the encounters table.
 * Adapts columns based on the layout mode determined by screen width.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { TableLayoutMode } from './types';
import { getGridTemplateColumns, GRID_GAP } from './types';

interface EncounterAuditsTableHeaderProps {
  /** Layout mode controlling which columns are visible */
  layout: TableLayoutMode;
}

export const EncounterAuditsTableHeader: React.FC<EncounterAuditsTableHeaderProps> = ({
  layout,
}) => {
  const theme = useTheme();

  // Card mode doesn't have a table header
  if (layout === 'card') return null;

  const headerStyle = {
    fontWeight: 600,
    color: theme.palette.text.secondary,
  };

  const showServiceType = layout === 'full' || layout === 'standard';
  const showBillingCodes = layout === 'full' || layout === 'standard';
  const showQuality = layout === 'full';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: getGridTemplateColumns(layout),
        gap: GRID_GAP,
        alignItems: 'center',
        px: 2,
        py: 1.5,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      {/* Icon spacer */}
      <Box />

      {/* Date */}
      <Typography variant="caption" sx={headerStyle}>
        Date
      </Typography>

      {/* Clinician */}
      <Typography variant="caption" sx={headerStyle}>
        Clinician
      </Typography>

      {/* Patient */}
      <Typography variant="caption" sx={headerStyle}>
        Patient
      </Typography>

      {/* Service Type - hidden on compact */}
      {showServiceType && (
        <Typography variant="caption" sx={headerStyle}>
          Service Type
        </Typography>
      )}

      {/* Billing Codes - hidden on compact */}
      {showBillingCodes && (
        <Typography variant="caption" sx={headerStyle}>
          Billing Codes
        </Typography>
      )}

      {/* Status */}
      <Typography variant="caption" sx={headerStyle}>
        Status
      </Typography>

      {/* Compliance */}
      <Typography variant="caption" sx={headerStyle}>
        Compliance
      </Typography>

      {/* Quality - only on full */}
      {showQuality && (
        <Typography variant="caption" sx={headerStyle}>
          Quality
        </Typography>
      )}

      {/* Chevron spacer */}
      <Box />
    </Box>
  );
};
